import { Immutable } from "immer";
import React, { useEffect } from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import iconXClose from "../../../../img/icons/x-close.svg";
import { Form } from "../../../forms/Form";
import { FormSubmit } from "../../../forms/FormSubmit";
import {
    IFormUUID,
    IReviewsProductID,
    IReviewsProductTypeID,
    IReviewsProductVariantID,
    WebPageURL,
} from "../../../models/nominals";
import { IReviewsProduct } from "../../../models/reviews";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { notEmpty, unique } from "../../../utils/functional";
import { navigateToURL } from "../../../utils/urls";
import { WriteReviewFormDisclaimers } from "../WriteReviewFormDisclaimers";
import { WriteReviewFormFields } from "../WriteReviewFormFields";
import { WriteReviewFormHeader } from "../WriteReviewFormHeader";
import { WriteReviewFormProductSelect } from "../WriteReviewFormProductSelect";
import {
    NOT_SURE_VARIANT_CHOICE_SLUG,
    REVIEW_VARIANT_NOT_SELECTED,
    SubmitReviewStatus,
    WriteReviewFormMode,
} from "../constants";
import { reviewFormVariantChange, setWriteReviewFormMode } from "../reducers";
import {
    getWriteReviewFormHTMLID,
    getWriteReviewFormProducts,
    getWriteReviewFormState,
    getWriteReviewFormVariantChoices,
    getWriteReviewTemplateForForm,
} from "../selectors";
import {
    loadReviewTemplate,
    loadReviewTemplateByProductType,
    submitReview,
} from "../thunks";

const scrollToProductSelect = (): void => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const productSelectDiv = document.querySelector(".wrf-product-select-form");
    productSelectDiv?.scrollIntoView({ behavior: "smooth" });
};

const scrollToRatingTitle = (): void => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const formDiv = document.querySelector(".wrf-rating__title");
    if (formDiv) {
        // Without the setTimeout, the page scroll stop at the wrong position(higher position) on Safari.
        // This issue occurs on only Safari. It looks scrollIntoView is fired before the client loaded
        // so setTimeout is added to fire scrollIntoView after the component loaded.
        setTimeout(() => {
            formDiv.scrollIntoView(true);
        }, 50);
    }
};

const getProductTypes = (
    products: ReadonlyArray<IReviewsProduct>,
): IReviewsProductTypeID[] => {
    return products
        .map((p) => p.product_type)
        .filter(unique)
        .filter(notEmpty);
};

const findVariantSlug = (
    products: ReadonlyArray<IReviewsProduct>,
    defaultProductVariantID: IReviewsProductVariantID,
): string => {
    return products.reduce((memo, product) => {
        const variant = product.variants.find((v) => {
            return v.id === defaultProductVariantID;
        });
        return variant ? variant.slug : memo;
    }, "");
};

export const WriteReviewForm = (
    props: Immutable<{
        formUUID: IFormUUID;
        formTitle?: string;
        brand?: string;
        displayedProductTypeIDWhitelist?: readonly IReviewsProductTypeID[];
        defaultProductTypeID?: IReviewsProductTypeID;
        defaultProductID?: IReviewsProductID;
        defaultProductVariantID?: IReviewsProductVariantID;
        returnURL?: WebPageURL;
    }> & {
        onReviewSubmit?: () => void;
    },
): React.ReactNode => {
    const dispatch = useAppDispatch();

    const state = useAppSelector((rootState) => rootState.reviews);
    const formTemplate = getWriteReviewTemplateForForm(state, props);
    const products = getWriteReviewFormProducts(state, props);
    const formState = getWriteReviewFormState(state, props);
    const variantChoices = getWriteReviewFormVariantChoices(state, props);
    const writeReviewTemplateProductID = formTemplate.productID;
    const writeReviewTemplate = formTemplate.template;

    const setDefaultProductSelection = (): void => {
        // If we already have a product selected, do nothing.
        if (writeReviewTemplateProductID || products.length <= 0) {
            return;
        }

        // Select the default product if we have one.
        if (props.defaultProductID) {
            void dispatch(
                loadReviewTemplate({
                    formUUID: props.formUUID,
                    productID: props.defaultProductID,
                }),
            );
            return;
        }

        // Since selected product implies ProductType, only set the default product type if we don't have a default product
        if (props.defaultProductTypeID) {
            void dispatch(
                loadReviewTemplateByProductType({
                    formUUID: props.formUUID,
                    products,
                    productTypeID: props.defaultProductTypeID,
                }),
            );
            return;
        }

        // If there's only a single product type for the given choices, return the first product for that type.
        const productTypes = getProductTypes(products);
        if (productTypes.length === 1) {
            void dispatch(
                loadReviewTemplateByProductType({
                    formUUID: props.formUUID,
                    products,
                    productTypeID: productTypes[0],
                }),
            );
            return;
        }
    };

    const setDefaultVariantSelection = (): void => {
        // If we already have a variant selected, do nothing.
        if (
            !writeReviewTemplateProductID ||
            products.length <= 0 ||
            formState.selectedVariant
        ) {
            return;
        }

        // Set the default product variant, if given
        if (props.defaultProductVariantID) {
            const variantSlug = findVariantSlug(
                products,
                props.defaultProductVariantID,
            );
            if (variantSlug) {
                dispatch(
                    reviewFormVariantChange({
                        formUUID: props.formUUID,
                        selectedVariant: variantSlug,
                    }),
                );
            }
        }
    };

    // Handle initial mount and updates
    useEffect(() => {
        setDefaultProductSelection();
        setDefaultVariantSelection();
    });

    // Handle scroll on mount when defaults are provided
    useEffect(() => {
        if (props.defaultProductID || props.defaultProductVariantID) {
            scrollToRatingTitle();
        }
    }, [props.defaultProductID, props.defaultProductVariantID]);

    const handleSubmit = async (
        e: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        e.preventDefault();
        const isMultiReview = false;

        // Compute the correct variant value for submission
        let submittedVariant = formState.selectedVariant;

        // if submitting blank variant for a product with variant choices, submit "not-selected", which throws error
        if (submittedVariant === "" && variantChoices.length > 1) {
            submittedVariant = REVIEW_VARIANT_NOT_SELECTED;
        }

        // if submitting "not-sure" variant for a product with variant choices, submit ""
        if (submittedVariant === NOT_SURE_VARIANT_CHOICE_SLUG) {
            submittedVariant = "";
        }

        // Create a modified form state with the correct variant for submission
        const currentFormState = {
            ...formState,
            selectedVariant: submittedVariant,
        };

        // Attempt to submit review
        const result = await dispatch(
            submitReview({
                formUUID: props.formUUID,
                template: writeReviewTemplate,
                form: currentFormState,
                isMultiReview,
            }),
        );

        if (!submitReview.fulfilled.match(result)) {
            return;
        }
        const submitStatus = result.payload.status;

        switch (submitStatus) {
            // Product selection errors?
            case SubmitReviewStatus.FAIL_MISSING_PRODUCT_SELECTION:
            case SubmitReviewStatus.FAIL_MISSING_VARIANT_SELECTION:
                scrollToProductSelect();
                break;

            // Success?
            case SubmitReviewStatus.SUCCESS:
                if (props.onReviewSubmit) {
                    props.onReviewSubmit();
                }
                break;
        }
    };

    const handleClose = (): void => {
        if (props.returnURL) {
            // Simulate a button click to return page
            navigateToURL(props.returnURL);
            return;
        }
        dispatch(
            setWriteReviewFormMode({
                formUUID: props.formUUID,
                mode: WriteReviewFormMode.CLOSED,
            }),
        );
    };

    if (formState.mode !== WriteReviewFormMode.FORM_OPEN) {
        return null;
    }

    return (
        <div
            id={getWriteReviewFormHTMLID(props.formUUID)}
            className="write-review-form"
            data-form-uuid={props.formUUID}
        >
            <button
                className="write-review-form__close"
                onClick={handleClose}
                aria-label={t`close`}
            >
                <SVG
                    className="write-review-form__close-icon"
                    src={iconXClose}
                    role="img"
                    aria-hidden="true"
                />
            </button>
            <WriteReviewFormHeader
                formTitle={props.formTitle || t`Write a Review`}
            />
            <Form onSubmit={handleSubmit} noValidate={true}>
                <WriteReviewFormProductSelect
                    formUUID={props.formUUID}
                    displayedProductTypeIDWhitelist={
                        props.displayedProductTypeIDWhitelist
                    }
                />
                <WriteReviewFormFields
                    formUUID={props.formUUID}
                    displayedProductTypeIDWhitelist={
                        props.displayedProductTypeIDWhitelist
                    }
                    brand={props.brand}
                />
                <WriteReviewFormDisclaimers />
                <FormSubmit
                    className="button button--rock-blue write-review-form__cta"
                    type="submit"
                    value={t`Submit your review`}
                />
            </Form>
        </div>
    );
};
